import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === null) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    setToken(null);
    setUser(null);
    navigate('/login');
  }, [navigate]);

  const fetchUserProfile = useCallback(async (currentToken) => {
    if (!currentToken) {
      setLoading(false);
      return null;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${currentToken}` }
      });
      
      if (!response.ok) {
        // Check if it's a 401 unauthorized error
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        }
        throw new Error('Failed to fetch user profile.');
      }
      
      const userData = await response.json();
      localStorage.setItem('authUser', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      logout();
      return null;
    }
  }, [logout]);

  useEffect(() => {
    const validateSession = async () => {
      setLoading(true);
      const storedToken = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('authUser');
      
      if (storedToken) {
        setToken(storedToken);
        
        // Use stored user data immediately for better UX
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
          } catch (e) {
            console.error('Error parsing stored user data:', e);
          }
        }
        
        // Then fetch fresh user data in background
        await fetchUserProfile(storedToken);
      }
      setLoading(false);
    };
    validateSession();
  }, [fetchUserProfile]);

  const navigateToDashboard = useCallback((userData) => {
    if (!userData || !userData.role) {
        navigate('/');
        return;
    }
    
    const isAdmin = userData.role !== 'recipient' && userData.role !== 'donor' && userData.role !== 'organization';
    
    if (isAdmin) {
        navigate('/admin/dashboard');
    } else if (userData.role === 'recipient') {
        // Check if onboarding is complete for recipients
        if (userData.isOnboardingComplete) {
            navigate('/recipient/dashboard');
        } else {
            navigate('/onboarding');
        }
    } else if (userData.role === 'organization') {
        // Check if onboarding is complete for organizations
        if (userData.isOnboardingComplete) {
            navigate('/organization/dashboard');
        } else {
            navigate('/onboarding');
        }
    } else if (userData.role === 'donor') {
        navigate('/');
    } else {
        navigate('/');
    }
  }, [navigate]);
  
  const setUserSession = useCallback(async (newToken, userData = null) => {
    localStorage.setItem('authToken', newToken);
    setToken(newToken);
    
    // If user data is provided, use it immediately
    if (userData) {
      localStorage.setItem('authUser', JSON.stringify(userData));
      setUser(userData);
      navigateToDashboard(userData);
    }
    
    // Always fetch fresh user data to ensure consistency
    const fullUser = await fetchUserProfile(newToken);
    if (fullUser) {
      navigateToDashboard(fullUser);
    }
  }, [fetchUserProfile, navigateToDashboard]);

  const login = useCallback(async (email, password, turnstileToken) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, turnstileToken }),
      });
      
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }
      
      await setUserSession(data.token, data.user);
      return data;
    } catch (error) {
      throw error;
    }
  }, [setUserSession]);
  
  const loginWithToken = useCallback((newToken, userObject) => {
    localStorage.setItem('authToken', newToken);
    localStorage.setItem('authUser', JSON.stringify(userObject));
    setToken(newToken);
    setUser(userObject);
    navigateToDashboard(userObject);
  }, [navigateToDashboard]);

  const refreshUserProfile = useCallback(async () => {
    const currentToken = localStorage.getItem('authToken');
    if (currentToken) {
      return await fetchUserProfile(currentToken);
    }
    return null;
  }, [fetchUserProfile]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    loginWithToken,
    fetchUserProfile: refreshUserProfile,
    setUserSession,
    navigateToDashboard
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};